import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../index";

// Base selectors
export const getMissionsState = (state: RootState) => state.missions;
export const getMastersById = (state: RootState) => state.missions.masters.byId;
export const getMasterIds = (state: RootState) => state.missions.masters.allIds;
export const getVersionsByMasterId = (state: RootState) =>
  state.missions.versions.byMasterId;
export const getVersionsLoading = (state: RootState) =>
  state.missions.versions.loading;
export const getSelectedMasterId = (state: RootState) =>
  state.missions.selectedMasterId;
export const getSelectedVersionId = (state: RootState) =>
  state.missions.selectedVersionId;
export const getMissionsLoading = (state: RootState) => state.missions.loading;
export const getMissionsError = (state: RootState) => state.missions.error;

// Memoized selectors
export const getAllMasters = createSelector(
  [getMastersById, getMasterIds],
  (byId, allIds) => allIds.map((id) => byId[id])
);

// Selector factory for getting a specific master by ID
export const getMasterById = (masterId: string) =>
  createSelector([getMastersById], (byId) => byId[masterId]);

// Selector factory for getting versions of a specific master
export const getVersions = (masterId: string) =>
  createSelector(
    [getVersionsByMasterId],
    (byMasterId) => byMasterId[masterId] || []
  );

// Selector factory for getting latest version of a master
export const getLatestVersion = (masterId: string) =>
  createSelector([getVersions(masterId)], (versions) => {
    if (!versions || versions.length === 0) return null;
    return versions.reduce((latest, version) => {
      const vNum = (version as any).versionNumber ?? version.version_number;
      const lNum = (latest as any).versionNumber ?? latest.version_number;
      return vNum > lNum ? version : latest;
    });
  });

// Get currently selected master
export const getSelectedMaster = createSelector(
  [getMastersById, getSelectedMasterId],
  (byId, selectedId) => (selectedId ? byId[selectedId] : null)
);

// Get currently selected version
export const getSelectedVersion = createSelector(
  [getVersionsByMasterId, getSelectedMasterId, getSelectedVersionId],
  (byMasterId, masterId, versionId) => {
    if (!masterId || !versionId) return null;
    const versions = byMasterId[masterId] || [];
    return versions.find((v) => v.id === versionId) || null;
  }
);

// Filter masters by semantic map
export const getMastersBySemanticMap = (semanticMapId: string) =>
  createSelector([getAllMasters], (masters) =>
    masters.filter((m) => m.semantic_map === semanticMapId)
  );

// Check if versions are loaded for a master
export const areVersionsLoaded = (masterId: string) =>
  createSelector(
    [getVersionsByMasterId],
    (byMasterId) => !!byMasterId[masterId]
  );

// Check if versions are currently loading for a master
export const areVersionsLoading = (masterId: string) => (state: RootState) =>
  state.missions.versions.loading[masterId] || false;

// Get count of masters
export const getMastersCount = createSelector(
  [getMasterIds],
  (allIds) => allIds.length
);

// Get count of versions for a specific master
export const getVersionsCount = (masterId: string) =>
  createSelector([getVersions(masterId)], (versions) => versions.length);

// Check if there are any masters
export const hasMasters = createSelector(
  [getMasterIds],
  (allIds) => allIds.length > 0
);

// Get all masters with their latest versions
export const getMastersWithLatestVersions = createSelector(
  [getAllMasters, getVersionsByMasterId],
  (masters, versionsByMasterId) =>
    masters.map((master) => {
      const versions = versionsByMasterId[master.id] || [];
      const latestVersion =
        versions.length > 0
          ? versions.reduce((latest, version) => {
              const vNum =
                (version as any).versionNumber ?? version.version_number;
              const lNum =
                (latest as any).versionNumber ?? latest.version_number;
              return vNum > lNum ? version : latest;
            })
          : null;
      return {
        master,
        latestVersion,
        versionsCount: versions.length,
      };
    })
);

// Get masters grouped by semantic map
export const getMastersGroupedBySemanticMap = createSelector(
  [getAllMasters],
  (masters) => {
    const grouped: Record<string, MasterMission[]> = {};
    masters.forEach((master) => {
      const mapId = master.semantic_map;
      if (!grouped[mapId]) {
        grouped[mapId] = [];
      }
      grouped[mapId].push(master);
    });
    return grouped;
  }
);

// Get default version for a master (version with default: true)
export const getDefaultVersion = (masterId: string) =>
  createSelector(
    [getVersions(masterId)],
    (versions) => versions.find((v) => v.default) || null
  );

// Check if a specific version exists
export const hasVersion = (masterId: string, versionId: string) =>
  createSelector(
    [getVersions(masterId)],
    (versions) => !!versions.find((v) => v.id === versionId)
  );

// Get versions sorted by version_number (descending)
// API returns versionNumber (camelCase), fall back to version_number for type safety
export const getVersionsSortedDesc = (masterId: string) =>
  createSelector([getVersions(masterId)], (versions) =>
    [...versions].sort((a, b) => {
      const bNum = (b as any).versionNumber ?? b.version_number;
      const aNum = (a as any).versionNumber ?? a.version_number;
      return bNum - aNum;
    })
  );

// Get versions sorted by version_number (ascending)
export const getVersionsSortedAsc = (masterId: string) =>
  createSelector([getVersions(masterId)], (versions) =>
    [...versions].sort((a, b) => {
      const aNum = (a as any).versionNumber ?? a.version_number;
      const bNum = (b as any).versionNumber ?? b.version_number;
      return aNum - bNum;
    })
  );
